import { Link } from "react-router-dom";
import { ArrowRight, Clock, EyeOff, SlidersHorizontal, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import { ShieldWm } from "@/components/Watermark";
import { FadeUp, MaskedLine, Eyebrow, DrawLine, ParallaxImg } from "@/components/Reveal";
import QuoteCTA from "@/components/QuoteCTA";

const KHALID_HERO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_elite-transfer-italy/artifacts/n0b7skf4_IMG-20260521-WA0043%282%29.jpg";

const SERVICES = [
  { n: "01", t: "Airport transfers", d: "Service to and from Italy's main airports, with particular attention to punctuality." },
  { n: "02", t: "Intercity transfers", d: "Personalised journeys between Italian cities, also over long distances." },
  { n: "03", t: "Business travel", d: "Dedicated solutions for professionals, managers and companies that need a discreet, reliable service." },
  { n: "04", t: "Events and ceremonies", d: "Chauffeur service for weddings, events, receptions and special occasions." },
  { n: "05", t: "Private services", d: "Tailor-made journeys for business travel, private occasions and individual transfers." },
  { n: "06", t: "National & international transfers", d: "Transfers on request to all Italian regions and across Europe." },
  { n: "07", t: "Bespoke services", d: "The possibility of arranging tailor-made services based on each client's needs." },
];

const TRUST = [
  { icon: Clock, t: "On time", d: "Respect for agreed schedules." },
  { icon: EyeOff, t: "Discreet", d: "Privacy and professionalism during every transfer." },
  { icon: SlidersHorizontal, t: "Tailored", d: "A service built around the client's needs." },
  { icon: MapPin, t: "Nationwide", d: "Transfers throughout Italy." },
];

export default function ServiziEn() {
  return (
    <>
      <SEO
        title="Services and Quotes | Zaichauffer – Private Chauffeur"
        description="Airport transfers, business travel, events and ceremonies, intercity transfers and bespoke services with a private chauffeur throughout Italy. Request a quote."
      />
      <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 pb-20 pt-40 md:pb-28 md:pt-52" data-testid="servizi-hero">
      <ShieldWm className="-z-10 -right-24 top-10 w-[400px] opacity-[0.04]" />
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp><Eyebrow>Services · Quotes</Eyebrow></FadeUp>
            <h1 className="mt-10 font-serif text-4xl leading-[1.08] tracking-tight text-bone sm:text-5xl lg:text-6xl">
              <MaskedLine>A JOURNEY TAILORED TO YOU.</MaskedLine>
            </h1>
            <DrawLine className="mt-10" />
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-xl text-[15px] font-light leading-relaxed text-mute">
                Every transfer is different. Send us your travel details and you will receive a
                personalised quote.
              </p>
            </FadeUp>
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <FadeUp delay={0.25}>
              <ParallaxImg src={KHALID_HERO} alt="Khalid El Aissaoui, owner and chauffeur of Zaichauffer, standing next to the executive vehicle" className="aspect-[4/5] w-full" />
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-36" data-testid="servizi-list">
        <div>
          {SERVICES.map((s, i) => (
            <FadeUp key={s.n} delay={0.04 * i}>
              <div className="group border-t border-white/10 py-10 transition-[padding] duration-500 hover:pl-4 md:py-12">
                <div className="flex flex-col gap-4 md:flex-row md:items-baseline md:gap-14">
                  <span className="font-serif text-sm italic text-gold md:w-10">{s.n}</span>
                  <div className="max-w-2xl flex-1">
                    <h2 className="font-serif text-2xl tracking-wide text-bone transition-colors duration-300 group-hover:text-gold md:text-3xl">
                      {s.t.toUpperCase()}
                    </h2>
                    <p className="mt-4 text-sm font-light leading-relaxed text-mute">{s.d}</p>
                  </div>
                  <Link to="/en/get-a-quote" aria-label={`Request a quote for ${s.t}`} className="mt-2 hidden md:inline-flex md:opacity-0 md:transition-opacity md:duration-300 md:group-hover:opacity-100">
                    <ArrowRight size={20} className="text-gold" />
                  </Link>
                </div>
              </div>
            </FadeUp>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </section>

      <section className="border-y border-gold/10 bg-coal py-20 md:py-28" data-testid="trust-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST.map((t, i) => (
            <FadeUp key={t.t} delay={0.06 * i}>
              <div className="hairline mb-8 w-12" />
              <t.icon size={24} strokeWidth={1.1} className="text-gold" />
              <h3 className="mt-6 font-serif text-xl tracking-wide text-bone">{t.t.toUpperCase()}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-mute">{t.d}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <QuoteCTA
        title="ALREADY HAVE A DESTINATION?"
        text="Tell us where you need to be and when. We will reply with a tailored solution."
        button="Request your quote"
        to="/en/get-a-quote"
      />
    </>
  );
}
